import { ICropping, IField, IGenericFieldLayer, IGenericFieldLayerLoader, isFunction, isUndefinedOrNull } from "../../omnia"
import { BoundaryDrawingContext } from "./drawingContext"

import Layer = omnia.map.Layer

export class EditBoundaryCtrl {

    static create(): ng.IDirective {

        return omnia.defineDirective(EditBoundaryCtrl, "app/fields/edit/boundary/boundary.html", {
            viewModel: "="
        })

    }

    private _intervalPromise: ng.IPromise<any>

    private _ctxt: BoundaryDrawingContext
    private _isActive: boolean;

    public backgroundLayers: IGenericFieldLayer[]
    private _bglayertarget: IField | ICropping
    private _bglayer: IGenericFieldLayer
    private _bglayerinst: Layer

    static $inject = ["$scope", "$interval", "OmniaFieldLayerLoader"]

    constructor(private $scope: ng.IScope, private $interval: ng.IIntervalService, private layerLoader: IGenericFieldLayerLoader) {
    }

    get viewModel() {
        return this._ctxt
    }

    set viewModel(value) {
        this._ctxt = value
    }

    get isActive(): boolean {
        return this._ctxt && this._isActive
    }

    get isInvalid(): boolean {
        return this._ctxt && this._ctxt.isInvalid
    }

    get validationErrors(): string[] {
        return this._ctxt && this._ctxt.validationErrors
    }

    get isOpen(): boolean {
        return this.isActive || this.hasChanged || this.isInvalid
    }

    get isCropping(): boolean {
        return this._ctxt && this._ctxt.targetIsCropping
    }

    hasBoundary(): boolean {
        return this._ctxt && !this._ctxt.newBoundary
    }

    getEditCommandText(): string {
        if (this._ctxt) {
            return this._ctxt.getEditCommandText()
        }
    }

    get drawingLayer() {
        return this._ctxt ? this._ctxt.drawingLayer : null
    }

    loadBackgroundLayers() {

        const target = this.viewModel ? this.viewModel.target : undefined

        if (target && (this._bglayertarget === target)) {
            return
        }

        if (target) {
            this._bglayertarget = target
            this.layerLoader.getLayers(target).then(l => this.backgroundLayers = l)
        }
        else {
            this.backgroundLayers = undefined
        }

    }

    get backgroundLayer() {
        return this._bglayer
    }

    set backgroundLayer(layer) {

        this._bglayer = layer
        this._bglayerinst = null

        if (layer) {
            layer.getInstance().then(l => {
                l.opacity = 75
                this._ctxt.setBackgroundLayer(l)
                this._bglayerinst = l
            })
        }
        else {
            this._ctxt.setBackgroundLayer(null)
        }

    }

    get backgroundLayerInstance() {
        return this._bglayerinst
    }


    setActive(active: boolean = true) {

        if (active != this._isActive) {

            if (active && (!this.canEditBoundary())) {
                return;
            }

            var vm = this._ctxt

            if (!vm) {
                this._isActive = false
                return
            }

            if (active) {
                this.loadBackgroundLayers()
                vm.switchToMapView()
                active = this.startEdit()
            }

            this._isActive = active
            vm.isActive = active

        }

    }

    get hasChanged(): boolean {
        return this._ctxt && (this._ctxt.hasChanged || this._ctxt.deleted)
    }

    get initialArea(): number {
        return this._ctxt.initialArea / 10000;
    }

    get currentArea(): number {
        return this._ctxt.currentArea / 10000;
    }

    getDisplayArea() {
        const a = this.currentArea
        return (isUndefinedOrNull(a) || isNaN(a))
            ? this._ctxt.targetBoundaryArea
            : Math.round(a * 100) / 100
    }

    canCopyBoundaryArea() {
        const a = this._ctxt.copyBoundaryArea
        return a && isFunction(a.copy) && a.enabled && (this.getDisplayArea() > 0)
    }

    copyBoundaryArea() {
        this._ctxt.copyBoundaryArea.copy(this.getDisplayArea())
    }

    private startEdit() {

        if (this.deleted) {
            this._ctxt.undelete()
        }

        if (this._ctxt.startDrawing()) {

            this.resetApplyInterval(true);

            return true;

        }

    }

    canEditBoundary(): boolean {
        return this._ctxt && this._ctxt.canEditBoundary();
    }

    private canCommit() {

        return (this._isActive && this._ctxt.map.canCommit());

    }

    stop(cancel: boolean = true) {

        this.resetApplyInterval(false);

        if (cancel) {

            try {
                this._ctxt.stopDrawing(cancel);
            }
            catch (e) {
                console.error(e);
            }

        } else {

            this._ctxt.stopDrawing(cancel);

        }

        this.setActive(false);

        return true;

    }

    private resetApplyInterval(start: boolean) {

        if (this._intervalPromise) {
            this.$interval.cancel(this._intervalPromise);
        }

        if (start) {
            this._intervalPromise = this.$interval(() => this.$scope.$applyAsync(), 500);
        }

    }

    canZoom() {
        if (this.deleted) return false
        const feature = this._ctxt && this._ctxt.feature
        if (!feature) return false
        return true
    }

    zoom() {

        var vm = this._ctxt;
        if (!vm) return;

        var feature = vm.feature;
        if (!feature) return;

        vm.map.viewExtent(feature.getGeometry());
    }

    autoCropOnCommit(value: boolean): boolean {
        return this._ctxt && this._ctxt.autoCropOnCommit(value);
    }

    supportsAutoCrop(): boolean {
        return this._ctxt && this._ctxt.supportsAutoCrop();
    }

    canAutoCrop(): boolean {
        return this._ctxt && this._ctxt.canAutoCrop();
    }

    autoCrop(): boolean {
        return this._ctxt.autoCrop();
    }

    get deleted() {
        return this._ctxt.deleted
    }

    get commands() {
        return this._ctxt?.commands
    }

    delete() {
        if (this._ctxt.delete()) {
            this.resetApplyInterval(false)
            this.setActive(false)
        }
    }

    canDelete() {
        return this._ctxt.canDelete()
    }

}

